import React from "react";
import { IonBackButton, IonButtons, IonContent, IonHeader, IonPage, IonToolbar } from "@ionic/react";
import { useParams } from "react-router";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import HeaderTitle from "@components/common/HeaderTitle";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import { Icon_Add_Evidence } from "@assets/images";
import ProgramCardButton from "@components/programs/ProgramCardButton";
import { IEvidence, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import { useMemo } from "react";
import _ from "lodash";
import ProgramSkillButton from "@components/programs/ProgramSkillButton";
import { useProgramData } from "@hooks/data/useProgramData";
import { evidenceAtom, userAtom } from "@state/State";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { userRolesSelector } from "@state/UserState";
import * as ProgramUtils from "@utils/programUtils";
import Collapsible from "react-collapsible";
import { EventRegister } from "react-native-event-listeners";
import * as FirebaseService from "@controllers/FirebaseService";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import { useHistory } from "react-router-dom";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import * as SPAPRPUtils from "@utils/programs/SPAPRPUtils";
import * as StringUtils from "@utils/stringUtils";

const ProgramSkillsPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string; sectionSlug?: string }>();

  const { userPrograms } = useProgramData();
  const { currentProgramCycle } = useProgramCycles(params.programId);

  const user = useRecoilValueLoadable(userAtom);
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);

  const program: ProgramData = userPrograms.find((item) => item.ID === params.programId)!;
  const programRoles = allUserRoles.find((role) => role.programID === params.programId);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  let skillsForRole: ProgramSkill[] = [];
  if (ProgramUtils.hasProgramLevelSkills(program.ID)) {
    skillsForRole = ProgramUtils.filterSkillsByLevel(
      program.Skills ?? [],
      ProgramUtils.getSkillLevelForProgram(user.contents, program)
    );
  } else {
    skillsForRole = program.Roles?.find((role) => role.Name === userRoles[0])?.Skills ?? [];
  }

  if (params.sectionSlug) {
    skillsForRole = skillsForRole.filter((skill) => StringUtils.toSlug(skill.Section ?? "") === params.sectionSlug);
  }

  const sectionTitle = params.sectionSlug ? skillsForRole[0]?.Section : undefined;

  let sortedSkillsForRole: ProgramSkill[] = [];
  if (ProgramUtils.isNwasARC(program.ID)) {
    sortedSkillsForRole = _.sortBy(skillsForRole, [
      (skill) => {
        const numberRequiredInDuration = parseInt(skill.numberRequiredInDuration ?? "0", 10);
        return numberRequiredInDuration > 0 ? 0 : 1;
      },
      (skill) => skill.Name?.toLowerCase?.() ?? "",
    ]);
  } else {
    sortedSkillsForRole = _.sortBy(skillsForRole, "Name");
  }

  let programEvidence: IEvidence[] = [];
  let evidenceIncludingDrafts: IEvidence[] = [];
  let evidenceForCycle: IEvidence[] | undefined;

  if (allEvidence.state === "hasValue" && allEvidence.contents) {
    evidenceIncludingDrafts = allEvidence.contents.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const id = evidenceJSON?.programInfo?.programID || "";

      return id === program?.ID;
    });

    programEvidence = allEvidence.contents.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const id = evidenceJSON?.programInfo?.programID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return id === program?.ID && onHoldReset !== 1 && evidence.draft !== true;
    });

    if (currentProgramCycle) {
      evidenceForCycle = programEvidence.filter(
        (item) => item.evidenceJSON?.programInfo?.programCycle === currentProgramCycle
      );
    }

    evidenceIncludingDrafts = _.orderBy(evidenceIncludingDrafts, "date", "desc");
    programEvidence = _.orderBy(programEvidence, "date", "desc");
  }

  const addSkillEvidence = (preselectedSkill?: ProgramSkill) => {
    const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;
    const evidenceDefinitions = program["Evidence Definitions"] ?? [];
    const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program);
    const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program);
    const skills = ProgramUtils.getSkillsForRole(user.contents, program);
    const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "skills")!;

    const data = {
      program: program,
      ...(program.CompetenceActivity &&
        program.CompetenceActivity.length > 0 && { competences: program.CompetenceActivity }),
      ...(evidenceDefinitions.length > 0 &&
        usesAdditionalEvidenceDefinitions && {
          evidenceDefinitions: evidenceDefinition,
        }),
      ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
      ...(optionalStandards.length > 0 && { optionalStandards }),
      ...(skills.length > 0 && { skills }),
      ...(preselectedSkill && { preselectedSkill: preselectedSkill }),
      skill: null,
    };

    FirebaseService.logEvent("program_add_skill_evidence", {
      program: program.Name,
      programID: program.ID,
      skill: preselectedSkill?.Name,
    });

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const evidenceCardClicked = (item: IEvidence): void => {
    history.push(`/dashboard/program/${params.programId}/read/${item.id}`);
  };

  const skillsHaveTopSection = useMemo(
    () => sortedSkillsForRole.some((skill) => skill["Top section"]),
    [sortedSkillsForRole]
  );

  const evidenceWithSkills = useMemo(() => {
    if (ProgramUtils.isSPAPRP(program.ID)) {
      return evidenceIncludingDrafts.filter((evidence) => SPAPRPUtils.isSkillEvidence({ evidence, program }));
    }

    return evidenceIncludingDrafts.filter((evidence) => EvidenceUtils.evidenceHasSkills(evidence, program.ID));
  }, [evidenceIncludingDrafts, program.ID]);

  const subTitle =
    ProgramUtils.isRenfrew(program.ID) && params.sectionSlug === "cpgs"
      ? "Manage and evidence your CPGs here. CPGs with a target count will show a RAG lozenge. All CPGs will show a count beneath the CPG title."
      : program.SkillsPageHeader ?? undefined;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{sectionTitle ?? "Skills"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <ProgramInfoCard
          title={sectionTitle ?? "Skills"}
          subTitle={subTitle}
          isHTMLSubtitle
        >
          <ProgramCardButton
            buttonPressed={() => addSkillEvidence()}
            buttonText="Add new evidence"
            icon={Icon_Add_Evidence}
          />
          {skillsHaveTopSection ? (
            <ProgramSkillsCollapsible
              sortedSkillsForRole={sortedSkillsForRole}
              programEvidence={evidenceForCycle ?? programEvidence}
              program={program}
              addSkillEvidence={(skill) => addSkillEvidence(skill)}
            />
          ) : (
            <ProgramSkillsNoTopSection
              sortedSkillsForRole={sortedSkillsForRole}
              programEvidence={evidenceForCycle ?? programEvidence}
              program={program}
              addSkillEvidence={(skill) => addSkillEvidence(skill)}
            />
          )}
        </ProgramInfoCard>
        <ProgramInfoCard
          title="Evidence list"
          style={{ paddingTop: 14, paddingBottom: 8 }}
        >
          <ProgramCardButton
            buttonPressed={() => addSkillEvidence()}
            buttonText="Add new evidence"
            icon={Icon_Add_Evidence}
          />
          {evidenceWithSkills.length > 0 && (
            <div className="mt-3">
              {evidenceWithSkills.map((item) => (
                <ProgramEvidenceCard
                  key={item.id}
                  buttonPressed={() => evidenceCardClicked(item)}
                  programEvidence={item}
                  program={program}
                  progressCheckLocked={false}
                />
              ))}
            </div>
          )}
        </ProgramInfoCard>
      </IonContent>
    </IonPage>
  );
};

export default ProgramSkillsPage;

type ProgramSkillsCollapsibleProps = {
  sortedSkillsForRole: ProgramSkill[];
  programEvidence: IEvidence[];
  program: ProgramData;
  addSkillEvidence: (skill: ProgramSkill) => void;
};

const ProgramSkillsCollapsible: React.FC<ProgramSkillsCollapsibleProps> = (props) => {
  let mappedSkills: _.Dictionary<ProgramSkill[]>[] = [];

  const isSPAPRP = ProgramUtils.isSPAPRP(props.program.ID);

  if (props.sortedSkillsForRole[0]?.["Top section"]) {
    let groupedSkills: _.Dictionary<ProgramSkill[]>;

    if (isSPAPRP) {
      groupedSkills = _.groupBy(props.sortedSkillsForRole, (skill) => skill["Top section"]);
      mappedSkills = Object.keys(groupedSkills)
        .sort()
        .reverse()
        .map((key) => {
          return _.groupBy(
            groupedSkills[key]!.sort((a, b) => a["Top section"]!.localeCompare(b["Top section"]!)),
            (item) => item["Top section"]!
          );
        });
    } else {
      groupedSkills = _.groupBy(props.sortedSkillsForRole, (skill) => skill["Top section"]);
      mappedSkills = Object.keys(groupedSkills)
        .sort()
        .reverse()
        .map((key) => {
          return _.groupBy(
            groupedSkills[key]!.sort((a, b) => a["Top section"]!.localeCompare(b["Top section"]!)),
            (item) => item["Top section"]!
          );
        });
    }
  }

  return (
    <>
      {mappedSkills?.map((item) => {
        const topSectionKey = Object.keys(item)[0];
        const skillsArray = item[topSectionKey];

        return (
          <div
            key={topSectionKey}
            className="mt-4"
          >
            <Collapsible
              trigger={
                <div className="dashboardHeader">
                  <div className="accountCardTitle">{topSectionKey}</div>
                  <div className="ml-auto flex cursor-pointer items-center justify-center">
                    <div className="text-13px font-semibold tracking-[-0.05px] text-cta-blue">{"Expand"}</div>
                  </div>
                </div>
              }
              triggerWhenOpen={
                <div className="dashboardHeader">
                  <div className="accountCardTitle">{topSectionKey}</div>
                  <div className="ml-auto flex cursor-pointer items-center justify-center">
                    <div className="text-13px font-semibold tracking-[-0.05px] text-cta-blue">{"Collapse"}</div>
                  </div>
                </div>
              }
              open={true}
            >
              <div className="mt-2">
                {skillsArray?.map((skill: ProgramSkill) => (
                  <ProgramSkillButton
                    skill={skill}
                    key={skill.ID}
                    programEvidence={props.programEvidence}
                    program={props.program}
                    buttonPressed={() => {
                      props.addSkillEvidence(skill);
                    }}
                  />
                ))}
              </div>
            </Collapsible>
          </div>
        );
      })}
    </>
  );
};

type ProgramSkillsNoTopSectionProps = {
  sortedSkillsForRole: ProgramSkill[];
  programEvidence: IEvidence[];
  program: ProgramData;
  addSkillEvidence: (skill: ProgramSkill) => void;
};

const ProgramSkillsNoTopSection: React.FC<ProgramSkillsNoTopSectionProps> = (props) => {
  return (
    <div className="mt-4">
      {props.sortedSkillsForRole.map((skill) => (
        <ProgramSkillButton
          skill={skill}
          key={skill.ID}
          programEvidence={props.programEvidence}
          program={props.program}
          buttonPressed={() => props.addSkillEvidence(skill)}
        />
      ))}
    </div>
  );
};
